
import { store, hasKey, updateConfig, setApiKey, clearConversation } from './store'
import { sendMessage, stop } from './agent'
import { providerList, getProvider } from './providers'
import { skills } from './skills'
import { registerTools, unregisterTools } from './toolRegistry'
import { navigateToolset } from './tools/navigate'
import { DocAttachment } from './types'
import SpinnerIco from '../components/SpinnerIco.vue'
import micromark from 'micromark'

const MAX_PDF_BYTES = 20 * 1024 * 1024

export default {
	name: 'AiChat',
	components: { SpinnerIco },
	inject: [ 'base' ],
	data() {
		return {
			store,
			providerList,
			skills,
			draft: '',
			showSettings: false,
			keyDraft: store.config.apiKeys[store.config.provider] ?? '',
			providerId: store.config.provider,
			model: store.config.model,
			navToken: null as number | null,
			attachments: [] as DocAttachment[],
			dragging: false,
		}
	},
	computed: {
		needsKey() { return !hasKey() },
		running() { return store.running },
		error() { return store.error },
		models() { return getProvider(this.providerId).models },
		activeSkillId: {
			get() { return store.activeSkillId },
			set(v) { store.activeSkillId = v },
		},
		visibleMessages() {
			return store.messages.filter((m: any) =>
				!(m.role === 'user' && m.content.length && m.content.every((b: any) => b.type === 'tool_result')))
		},
	},
	watch: {
		'store.messages': { deep: true, handler() { this.scrollDown() } },
	},
	methods: {
		providerChanged() {
			this.model = getProvider(this.providerId).defaultModel
			this.keyDraft = store.config.apiKeys[this.providerId] ?? ''
		},
		save() {
			updateConfig({ provider: this.providerId, model: this.model })
			setApiKey(this.providerId, this.keyDraft.trim())
			this.showSettings = false
		},
		send() {
			const text = this.draft.trim()
			if ((!text && !this.attachments.length) || this.running) return
			const atts = this.attachments
			this.draft = ''
			this.attachments = []
			sendMessage(text, atts)
		},
		pickFile() { (this.$refs.file as HTMLInputElement).click() },
		onFilePick(e: any) {
			this.addFiles(e.target.files)
			e.target.value = ''
		},
		onDrop(e: any) {
			this.dragging = false
			this.addFiles(e.dataTransfer?.files)
		},
		addFiles(list: FileList | null | undefined) {
			for (const file of Array.from(list ?? [])) {
				if (file.type !== 'application/pdf') { store.error = 'Only PDF files are supported.'; continue }
				if (file.size > MAX_PDF_BYTES) { store.error = `"${ file.name }" is larger than 20 MB.`; continue }
				const reader = new FileReader()
				reader.onload = () => {
					const res = String(reader.result)
					const data = res.slice(res.indexOf(',') + 1)
					this.attachments.push({ name: file.name, mediaType: 'application/pdf', data })
				}
				reader.readAsDataURL(file)
			}
		},
		removeAttachment(i: number) { this.attachments.splice(i, 1) },
		doStop() { stop() },
		clear() { clearConversation() },
		md(text: string) { return micromark(String(text ?? '')) },
		hasArgs(input: any) { return input && Object.keys(input).length > 0 },
		compact(input: any) {
			const s = JSON.stringify(input)
			return s.length > 60 ? s.slice(0, 57) + '…' : s
		},
		scrollDown() {
			this.$nextTick(() => {
				const el = this.$refs.messages as HTMLElement
				if (el) el.scrollTop = el.scrollHeight
			})
		},
	},
	mounted() {
		this.navToken = registerTools(navigateToolset(this.base, (p: string) => this.$router.push(p)))
	},
	beforeDestroy() {
		if (this.navToken != null) unregisterTools(this.navToken)
	},
}
